import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { isOriginInUse } from '../../util/search';
import {
  DEFAULT_RADIUS_MI,
  boundsCenter,
  circleBounds,
  isWithinRadius,
  validRadius,
} from '../../util/searchArea';
import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { FormattedMessage } from '../../util/reactIntl';

import { ModalInMobile, Page } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  initialValues,
  validUrlQueryParamsFromProps,
  validFilterParams,
  getSearchPageResourceLocatorStringParams,
  getDerivedRenderData,
  onApplyFilters,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';

import SearchMap from './SearchMap/SearchMap';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';
import SearchPill, {
  searchPillInitialValues,
  searchPillToSearchParams,
} from './SearchPill/SearchPill';
import RadiusControl from './RadiusControl/RadiusControl';

import css from './SearchPage.module.css';
import { types as sdkTypes } from '../../util/sdkLoader';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.
const SEARCH_AREA_DEBOUNCE = 250; // The custom radius slider searches when it pauses.

/**
 * The search area (02_Search_Mobile/SPEC.md "Search radius") from the URL: a point and a radius.
 * The point is the searched place (origin), or the middle of the searched bounds. Without a
 * searched place there's no search area.
 *
 * @param {Object} searchParamsInURL parsed with latlng and latlngBounds
 * @returns {Object|null} { center, radiusMi, hasRadiusParam }
 */
const getSearchArea = searchParamsInURL => {
  const { bounds, origin, radius } = searchParamsInURL || {};
  const center = origin || boundsCenter(bounds);
  return center ? { center, radiusMi: validRadius(radius), hasRadiusParam: radius != null } : null;
};

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      // The radius while the custom slider is being dragged, before the search catches up
      draftRadiusMi: null,
      // Has the visitor moved the search area (after which the hint shows the count)
      hasMovedSearchArea: false,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // The mobile search pill (place and dates)
    this.handleSearchPillSubmit = this.handleSearchPillSubmit.bind(this);

    // The search area: radius control and tap to move
    this.searchWithSearchArea = this.searchWithSearchArea.bind(this);
    this.handleRadiusChange = this.handleRadiusChange.bind(this);
    this.handleSearchAreaMove = this.handleSearchAreaMove.bind(this);
    this.searchWithDraftRadius = debounce(
      radiusMi => this.searchWithSearchArea({ radiusMi }),
      SEARCH_AREA_DEBOUNCE
    );
  }

  componentDidMount() {
    this.applyDefaultRadiusMaybe();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      this.applyDefaultRadiusMaybe();
    }
  }

  componentWillUnmount() {
    this.searchWithDraftRadius.cancel();
  }

  // A search for a place (e.g. from the landing page) starts with the default radius around it,
  // so that the results are the sitters within that radius
  applyDefaultRadiusMaybe() {
    const searchParamsInURL = parse(this.props.location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    // A searched place has bounds (a place from the list, or the visitor's current location,
    // which has no address); a moved map has mapSearch
    const { bounds, mapSearch } = searchParamsInURL;
    const searchArea = getSearchArea(searchParamsInURL);
    if (bounds && !mapSearch && searchArea && !searchArea.hasRadiusParam) {
      this.searchWithSearchArea({ radiusMi: DEFAULT_RADIUS_MI }, { replace: true });
    }
  }

  // Search the listings within the radius of the point: the query uses the circle's bounds
  searchWithSearchArea({ center: newCenter, radiusMi: newRadius }, options = {}) {
    const { history, routeConfiguration, config, location } = this.props;
    const searchParamsInURL = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const current = getSearchArea(searchParamsInURL);
    const center = newCenter || current?.center;
    if (!center) {
      return;
    }
    const radiusMi = validRadius(newRadius || current?.radiusMi);

    // A moved search area is no longer the searched place: the place name goes
    const { address, ...validParams } = validUrlQueryParamsFromProps(this.props);
    const addressMaybe = newCenter ? {} : address ? { address } : {};
    const searchParams = {
      ...validParams,
      ...addressMaybe,
      bounds: circleBounds(center, radiusMi),
      ...(isOriginInUse(config) ? { origin: center } : {}),
      radius: radiusMi,
    };
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    const url = createResourceLocatorString(
      routeName,
      routeConfiguration,
      pathParams,
      searchParams
    );
    if (options.replace) {
      history.replace(url);
    } else {
      history.push(url);
    }
  }

  // 5 / 10 / 25 mi search right away; the custom slider waits for a pause
  handleRadiusChange(radiusMi, { isDraft = false } = {}) {
    this.setState({ hasMovedSearchArea: true, draftRadiusMi: isDraft ? radiusMi : null });
    if (isDraft) {
      this.searchWithDraftRadius(radiusMi);
    } else {
      this.searchWithDraftRadius.cancel();
      this.searchWithSearchArea({ radiusMi });
    }
  }

  // Tapping the map moves the search area there
  handleSearchAreaMove(center) {
    this.setState({ hasMovedSearchArea: true });
    this.searchWithSearchArea({ center });
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;
    const { params: currentPathParams } = this.props;

    const routes = this.props.routeConfiguration;
    const searchPagePath = currentPathParams.listingType
      ? pathByRouteName('SearchPageWithListingType', routes, currentPathParams)
      : pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    // With a search area (a point and a radius), moving the map doesn't search: tapping it does
    const hasSearchArea = !!parse(this.props.location.search).radius;

    if (viewportBoundsChanged && isSearchPage && !hasSearchArea) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      // parse query parameters, including a custom attribute named category
      // when onMapMoveEnd is called, pagination needs to be reset.
      const { address, bounds, mapSearch, page, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routes, location);

      history.push(createResourceLocatorString(routeName, routes, pathParams, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config, params: currentPathParams, location } = this.props;
    onApplyFilters({
      history,
      routeConfiguration,
      config,
      location,
      currentPathParams,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      currentQueryParams: this.state.currentQueryParams,
    });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  // Start over: no place, no dates, no radius (there are no other filters on this page)
  resetAll(e) {
    const { history, routeConfiguration, location } = this.props;
    this.searchWithDraftRadius.cancel();
    this.setState({ currentQueryParams: {}, draftRadiusMi: null, hasMovedSearchArea: false });
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, {}));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  handleSearchPillSubmit(values) {
    const { history, routeConfiguration, config, location } = this.props;
    const searchParams = {
      ...validUrlQueryParamsFromProps(this.props),
      ...searchPillToSearchParams(values, isOriginInUse(config)),
    };
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      searchParamsInURL,
      customSecondaryFilters,
      availablePrimaryFilters,
      availableFilters,
      hasSecondaryFilters,
      selectedFilters,
      selectedFiltersCountForMobile,
      isValidDatesFilter,
      selectedSecondaryFiltersCount,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    const {
      bounds = new SDKLatLngBounds(
        new SDKLatLng(52.40702368, -80.93326788),
        new SDKLatLng(13.3484246, -123.90175609)
      ),
      origin,
    } = searchParamsInURL || {};

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // The heading: "3 sitters near Franklin", or "3 sitters nearby" without a place
    const { address, dates } = parse(location.search);
    const place = typeof address === 'string' ? address.split(',')[0] : null;
    const searchPillValues = searchPillInitialValues({
      address,
      bounds: searchParamsInURL?.bounds,
      origin: searchParamsInURL?.origin,
      dates,
    });
    const isClosestFirst = isOriginInUse(config) && !!origin;

    // The search area, and the listings in it: the query covers the circle's bounding box, so
    // the listings in the box's corners are left out here
    const searchArea = getSearchArea(searchParamsInURL);
    const radiusMi = this.state.draftRadiusMi || searchArea?.radiusMi;
    const hasRadius = !!searchArea && !!parse(location.search).radius;
    const listingsInArea = hasRadius
      ? listings.filter(l => isWithinRadius(l.attributes?.geolocation, searchArea.center, radiusMi))
      : listings;
    // One page of results can be counted exactly; beyond that, the API's total is used
    const resultCount =
      hasRadius && (pagination?.totalPages || 1) <= 1 ? listingsInArea.length : totalItems;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div id="main-content" className={css.container} role="main">
          <div className={css.searchResultContainer}>
            <div className={css.mobileSearchBar}>
              <SearchPill
                variant="mobile"
                initialValues={searchPillValues}
                onSubmit={this.handleSearchPillSubmit}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
            <div className={css.resultsHeader}>
              <h1 className={css.resultsTitle}>
                {listingsAreLoaded ? (
                  place ? (
                    <FormattedMessage
                      id="SearchPage.resultsNear"
                      values={{ count: resultCount, place }}
                    />
                  ) : (
                    <FormattedMessage
                      id="SearchPage.resultsNearby"
                      values={{ count: resultCount }}
                    />
                  )
                ) : null}
              </h1>
              {isClosestFirst || hasRadius ? (
                <span className={css.resultsSort}>
                  {isClosestFirst ? <FormattedMessage id="SearchPage.closestFirst" /> : null}
                  {isClosestFirst && hasRadius ? ' · ' : null}
                  {hasRadius ? (
                    <FormattedMessage id="SearchPage.withinRadius" values={{ radius: radiusMi }} />
                  ) : null}
                </span>
              ) : null}
              {hasRadius ? (
                <button
                  type="button"
                  className={css.radiusLink}
                  onClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
                >
                  <FormattedMessage
                    id="SearchPage.withinRadiusLink"
                    values={{ radius: radiusMi }}
                  />
                </button>
              ) : null}
            </div>
            {listingsAreLoaded && resultCount === 0 ? (
              <div className={css.noResults}>{noResultsInfo}</div>
            ) : null}
            <div
              className={classNames(css.listingsForMapVariant, {
                [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
              })}
            >
              <SearchErrors
                searchListingsError={searchListingsError}
                isValidDatesFilter={isValidDatesFilter}
              />
              <SearchResultsPanel
                className={css.searchListingsPanel}
                listings={listingsInArea}
                pagination={listingsAreLoaded ? pagination : null}
                search={parse(location.search)}
                setActiveListing={onActivateListing}
                isMapVariant
                listingTypeParam={listingTypePathParam}
                intl={intl}
              />
            </div>
            {/* Mobile: the floating toggle to the map */}
            <button
              type="button"
              className={css.viewToggle}
              onClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
            >
              <FormattedMessage id="SearchPage.mapToggle" />
            </button>
          </div>
          <ModalInMobile
            className={css.mapPanel}
            id="SearchPage_map"
            isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mapWrapper} data-testid="searchMapContainer">
              {isMobileLayout && this.state.isSearchMapOpenOnMobile ? (
                <button
                  type="button"
                  className={classNames(css.viewToggle, css.viewToggleOnMap)}
                  onClick={() => {
                    this.setState({ isSearchMapOpenOnMobile: false });
                    onManageDisableScrolling('SearchPage_map', false);
                  }}
                >
                  <FormattedMessage id="SearchPage.listToggle" />
                </button>
              ) : null}
              {shouldShowSearchMap && hasRadius ? (
                <RadiusControl
                  className={css.mapOverlay}
                  radiusMi={radiusMi}
                  resultCount={resultCount}
                  showCount={this.state.hasMovedSearchArea && listingsAreLoaded}
                  isMobileLayout={isMobileLayout}
                  onChange={this.handleRadiusChange}
                />
              ) : null}
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  rootClassName={css.mapRoot}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                  location={location}
                  listings={listings || []}
                  searchArea={hasRadius ? { center: searchArea.center, radiusMi } : null}
                  onSearchAreaMove={this.handleSearchAreaMove}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPage_map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
        </div>
      </Page>
    );
  }
}

/**
 * SearchPage "container" (map layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const {
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = useSelector(state => state.SearchPage);
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const onActivateListing = useCallback(listingId => dispatch(setActiveListing(listingId)), [
    dispatch,
  ]);

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      activeListingId={activeListingId}
      onManageDisableScrolling={onManageDisableScrolling}
      onActivateListing={onActivateListing}
    />
  );
};

export default SearchPage;
